import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import { SiteFooter, SiteNav } from '../../components/SiteChrome';
import { CONTACT_EMAIL, List, Section } from '../Legal/LegalPage';

/**
 * Apple requires a reachable Support URL that actually offers a way to get help, so this
 * leads with the address rather than burying it under an FAQ. The questions below are the
 * ones whose answers live in the app — pointing at the screen is faster than a reply.
 */
export function Support() {
  const mailto = `mailto:${CONTACT_EMAIL}`;

  return (
    <>
      <Helmet>
        <title>Support — ClientBridge</title>
        <meta
          name="description"
          content="Get help with ClientBridge — contact us, or find how to log, edit and delete your client interactions."
        />
      </Helmet>

      <SiteNav />

      <main className="mx-auto max-w-[760px] px-6 pb-20 pt-8 md:px-10">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-warm-faint">
          We read every message
        </span>
        <h1 className="mt-3.5 font-display text-[38px] font-bold leading-[1.05] tracking-[-0.02em] text-forest-deep md:text-[46px]">
          Support
        </h1>

        <a
          href={mailto}
          className="mt-8 inline-flex items-center rounded-full bg-forest px-6 py-3.5 font-body text-[15px] font-semibold text-cream-light transition-colors hover:bg-forest-deep"
        >
          Email {CONTACT_EMAIL}
        </a>
        <p className="mt-3 font-body text-[14px] text-warm">
          We aim to reply within two business days.
        </p>

        <div className="legal mt-12">
          <Section heading="Common questions">
            <List
              items={[
                <>
                  <strong>How do I log an interaction?</strong> On the Home tab,
                  type a client name, add a phone number and a note, then tap{' '}
                  <strong>Save note</strong>. It appears on your timeline
                  straight away. The person icon beside the phone field pulls a
                  name and number from your address book if you would rather not
                  type them.
                </>,
                <>
                  <strong>How do I see everything for one client?</strong> Open
                  the <strong>Contacts</strong> tab and tap their name. That
                  screen lists every interaction you have logged with them,
                  newest first.
                </>,
                <>
                  <strong>How do I delete a single interaction?</strong> Tap the
                  trash icon on the right of any timeline row and confirm. It is
                  removed permanently.
                </>,
                <>
                  <strong>How do I delete my account?</strong> Open the{' '}
                  <strong>Settings</strong> tab and tap{' '}
                  <strong>Delete account</strong>. This erases your profile and
                  every interaction you have logged, immediately and
                  permanently. It cannot be undone.
                </>,
                <>
                  <strong>Can I get a copy of my data?</strong> Yes — email us
                  and we will send you an export of everything tied to your
                  account.
                </>,
                <>
                  <strong>I cannot sign in.</strong> ClientBridge uses Sign in
                  with Apple and Sign in with Google, so there is no ClientBridge
                  password to reset. If sign-in fails, check your connection and
                  try again, then write to us with the account you are using.
                </>,
              ]}
            />
          </Section>

          <Section heading="Privacy and your data">
            <p>
              Your client records are locked to your account and unreadable by
              anyone else. The full detail is in our{' '}
              <Link to="/privacy">Privacy Policy</Link>, and the rules of use are
              in our <Link to="/terms">Terms of Service</Link>.
            </p>
          </Section>

          <Section heading="Reporting a problem">
            <p>
              Tell us what you were doing, what you expected, and what happened
              instead — plus your device and iOS version if you have them. That
              is usually enough for us to reproduce it without a second round
              trip. Write to <a href={mailto}>{CONTACT_EMAIL}</a>.
            </p>
          </Section>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}
